// WorkspaceSettings.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid2,
    Divider,
    IconButton,
    Snackbar,
    Alert,
    Tooltip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { usePermission } from '../authentication/permission-providor/UserPermission';
import useMediaQuery from '@mui/material/useMediaQuery';
import AddButton from "../../themes/overrides/AddButton";
import ManageUsersModal from "../../components/ManageUserModal";
import WorkspaceModal from './WorkspaceModal';
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal";
import LoadingOverlay from '../../components/LoadingOverlay';
import ManageAPIKeysModal from '../../components/ManageAPIKeysModal';
import ManageLocalesModal from '../../components/ManageLocalesModal';
import ManageWebhooks from '../../components/webhooks/ManageWebhooks';


import ManageAIModal from '../../components/ManageAIModal';

import userIcon from "../../assets/images/profile-2user.svg";
import editIcon from "../../assets/images/edit-2.svg";
import CopyIcon from '../../components/icons/CopyIcon';
import DeleteButton from '../../themes/overrides/DeleteButton';
import { useDispatch, useSelector } from 'react-redux';
import { removeWorkspace, getSpecificWorkspace } from '../../actions/workspaceAction';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';

const WorkspaceSettings = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false);
    const [shouldRefresh, setShouldRefresh] = useState(true);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [manageUsersModalOpen, setManageUsersModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [manageAPIPopup, setManageAPIPopup] = useState(false);
    const [manageLocalesPopup, setManageLocalesPopup] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { hasPermission } = usePermission();
    const [manageAIPopup, setManageAIPopup] = useState(false);
    const [manageWebhooksPopup, setManageWebhooksPopup] = useState(false);
    const { workspaceId } = useParams();
    const currentWorkspace = useSelector((state) => state.workspace.workspaceInfo)

    useEffect(() => {
        if (shouldRefresh) {
            dispatch(getSpecificWorkspace(workspaceId));
            setShouldRefresh(false);
        }
    }, [shouldRefresh, dispatch, workspaceId]);

    const handleManageUsers = () => {
        setManageUsersModalOpen(true);
    };

    const handleDelete = () => {
        setDeleteModalOpen(true);
    };

    const handleEdit = () => {
        setIsEditModalOpen(true);
    };

    const handleManageApis = () => {
        setManageAPIPopup(true);
    }

    const handleManageLocales = () => {
        setManageLocalesPopup(true)
    }

    const handleManageAIAPIs = () => {
        setManageAIPopup(true)
    };

    const handleManageWebhooks = () => {
        setManageWebhooksPopup(true);
    }


    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(workspaceId);
            showSnackbar('Workspace Id copied to clipboard!', 'success');
        } catch (err) {
            showSnackbar('Failed to copy', 'error');
        }
    }

    const showSnackbar = (message, severity) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setOpenSnackbar(true);
    };

    const handleDeleteConfirm = async () => {
        setLoading(true)
        try {
            await dispatch(removeWorkspace(currentWorkspace?.data?._id, navigate))
                .then(() => {
                    setDeleteModalOpen(false);
                })
        } catch (error) {
            console.log('Delete failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };


    return (
        <Grid2 container spacing={2} alignItems="center" >
            <Grid2 container size={12} display="flex" justifyContent="space-between">
                <Grid2 size={{ xs: 12, md: 8 }} display='flex' alignItems="flex-start" sx={{ marginLeft: { xs: 4, md: 0 } }}>
                    {/* <Box component="img"  sx={{ width:{xs: 32 ,md: 40 }, marginRight:{xs:2, md:4}}} src={WorkspaceIcon}/> */}
                    <Typography variant={"h1"}> Workspace Settings</Typography>
                </Grid2>
            </Grid2>
            <Grid2 spacing={4} container>
                <Grid2 size={12}>
                    <Box sx={{
                        p: 3,
                        border: '1px solid #ECECEC',
                        borderRadius: 2,
                        backgroundColor: '#fff'
                    }}>
                        <Box sx={{ display: { xs: 'block', md: 'flex' }, justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h2">Manage Users</Typography>
                            <AddButton
                                startIcon={<img src={userIcon} alt="Manage Users" style={{ width: 24, height: 24 }} />}
                                label="Manage Users"
                                onClick={handleManageUsers}
                                sx={{ mt: { xs: 2, md: 0 } }}
                                disabled={!hasPermission('admin')}
                            />
                        </Box>
                        <Divider />
                        <Typography sx={{ mt: 2, color: '#ACACAC' }}>
                            Add or remove users from your workspace
                        </Typography>
                    </Box>
                </Grid2>

                <Grid2 size={12}>
                    <Box sx={{
                        p: 3,
                        border: '1px solid #ECECEC',
                        borderRadius: 2,
                        backgroundColor: '#fff'
                    }}>
                        <Box sx={{ display: { xs: 'block', md: 'flex' }, justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 2
                                }}
                            >
                                <Typography variant="h2" >
                                    Edit Workspace
                                </Typography>
                                <Tooltip title="Copy Workspace ID" arrow>
                                    <IconButton
                                        variant=""
                                        sx={{
                                            minHeight: 40,
                                            alignSelf: 'center',
                                            marginLeft: { xs: 0, md: 1 },
                                        }}
                                        onClick={handleCopy}
                                    >
                                        <CopyIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <AddButton
                                startIcon={<img src={editIcon} alt="Edit" style={{ width: 24, height: 24 }} />}
                                label="Edit Details"
                                onClick={handleEdit}
                                disabled={!hasPermission('admin')}
                            />
                        </Box>
                        <Divider />
                        <Typography sx={{ mt: 2, color: '#ACACAC' }}>
                            Update workspace name and other details
                        </Typography>
                    </Box>
                </Grid2>
                <Grid2 size={12}>
                    <Box sx={{
                        p: 3,
                        border: '1px solid #ECECEC',
                        borderRadius: 2,
                        backgroundColor: '#fff'
                    }}>
                        <Box sx={{ display: { xs: 'block', md: 'flex' }, justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h2">Generate API</Typography>
                            <AddButton
                                startIcon={<img src={editIcon} alt="Edit" style={{ width: 24, height: 24 }} />}
                                label="Manage APIs"
                                onClick={handleManageApis}
                                sx={{ mt: { xs: 2, md: 0 } }}
                                disabled={!hasPermission('admin')}
                            />
                        </Box>
                        <Divider />
                        <Typography sx={{ mt: 2, color: '#ACACAC' }}>
                            Manage your API Keys
                        </Typography>
                    </Box>
                </Grid2>
                <Grid2 size={12}>
                    <Box sx={{
                        p: 3,
                        border: '1px solid #ECECEC',
                        borderRadius: 2,
                        backgroundColor: '#fff'
                    }}>
                        <Box sx={{ display: { xs: 'block', md: 'flex' }, justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h2">Add Locales</Typography>
                            <AddButton
                                startIcon={<img src={editIcon} alt="Edit" style={{ width: 24, height: 24 }} />}
                                label="Manage Locales"
                                onClick={handleManageLocales}
                                sx={{ mt: { xs: 2, md: 0 } }}
                                disabled={!hasPermission('admin')}
                            />
                        </Box>
                        <Divider />
                        <Typography sx={{ mt: 2, color: '#ACACAC' }}>
                            Manage your Locales
                        </Typography>
                    </Box>
                </Grid2>
                 <Grid2 size={12}>
                    <Box sx={{
                        p: 3,
                        border: '1px solid #ECECEC',
                        borderRadius: 2,
                        backgroundColor: '#fff'
                    }}>
                        <Box sx={{ display: { xs: 'block', md: 'flex' }, justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h2">WebHook</Typography>
                            <AddButton
                                startIcon={<img src={editIcon} alt="Edit" style={{ width: 24, height: 24 }} />}
                                label="Manage Webhooks"
                                onClick={handleManageWebhooks}
                                sx={{ mt: { xs: 2, md: 0 } }}
                                disabled={!hasPermission('admin')}
                            />
                        </Box>
                        <Divider />
                        <Typography sx={{ mt: 2, color: '#ACACAC' }}>
                            Manage your webhooks
                        </Typography>
                    </Box>
                </Grid2>

                <Grid2 size={12}>
                    <Box sx={{
                        p: 3,
                        border: '1px solid #ECECEC',
                        borderRadius: 2,
                        backgroundColor: '#fff'
                    }}>
                        <Box sx={{ display: { xs: 'block', md: 'flex' }, justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h2">Manage AI Integration</Typography>
                            <AddButton
                                startIcon={<img src={editIcon} alt="Edit" style={{ width: 24, height: 24 }} />}
                                label="Configure AI APIs"
                                onClick={handleManageAIAPIs}
                                sx={{ mt: { xs: 2, md: 0 } }}
                                disabled={!hasPermission('admin')}
                            />
                        </Box>
                        <Divider />
                        <Typography sx={{ mt: 2, color: '#ACACAC' }}>
                            Configure and manage your AI APIs
                        </Typography>
                    </Box>
                </Grid2>

                <Grid2 size={12}>
                    <Box sx={{
                        p: 3,
                        border: '1px solid #ECECEC',
                        borderRadius: 2,
                        backgroundColor: '#fff'
                    }}>
                        <Box sx={{ display: { xs: 'block', md: 'flex' }, justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h2">Delete Workspace</Typography>
                            <DeleteButton
                                sx={{ color: "#960101", border: "1.5px solid", mt: { xs: 2, md: 0 } }}
                                label="Delete Workspace"
                                onClick={handleDelete}
                                iconColor={!hasPermission('admin') ? '#ACACAC' : '#960101'}
                                disabled={!hasPermission('admin')}
                            />

                        </Box>
                        <Divider />
                        <Typography sx={{ mt: 2, color: '#ACACAC' }}>
                            Permanently delete this workspace and all its contents
                        </Typography>
                    </Box>
                </Grid2>
            </Grid2>

            <ManageUsersModal
                open={manageUsersModalOpen}
                onClose={() => setManageUsersModalOpen(false)}
                selectedType={currentWorkspace?.data}
                onSuccess={() => setShouldRefresh(true)}
            />

            <WorkspaceModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                currentWorkspace={currentWorkspace}
                refreshList={() => setShouldRefresh(true)}
                isEdit={true}
            />

            <DeleteConfirmationModal
                deleteButtonLabel={'Delete'}
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                text={
                    <>
                        <span className="font-normal">Are you sure, you want to permanently delete the workspace</span> {`"${currentWorkspace?.data?.name}" `}
                        <span className="font-normal">and all its contents
                            ?</span>
                    </>
                }
                onConfirm={handleDeleteConfirm}
            />
            <ManageAPIKeysModal
                open={manageAPIPopup}
                onClose={() => setManageAPIPopup(false)}
                selectedType={currentWorkspace?.data}
                onSuccess={() => setShouldRefresh(true)}
            />

            <ManageLocalesModal
                open={manageLocalesPopup}
                onClose={() => setManageLocalesPopup(false)}
                selectedType={currentWorkspace?.data}
                onSuccess={() => setShouldRefresh(true)}
            />

            <ManageAIModal
                open={manageAIPopup}
                onClose={() => setManageAIPopup(false)}
                selectedType={currentWorkspace?.data}
                onSuccess={() => setShouldRefresh(true)}
            />

            <ManageWebhooks
                open={manageWebhooksPopup}
                onClose={() => setManageWebhooksPopup(false)}
                selectedType={currentWorkspace?.data}
                onSuccess={() => setShouldRefresh(true)}
            />

            <Snackbar
                open={openSnackbar}
                autoHideDuration={3000}
                onClose={() => setOpenSnackbar(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setOpenSnackbar(false)}
                    severity={snackbarSeverity}
                    sx={{ width: '100%' }}
                >{snackbarMessage}</Alert>

            </Snackbar>



            {loading && <LoadingOverlay />}

        </Grid2>
    );
};

export default WorkspaceSettings;